import * as Yup from "yup";
import moment from "moment";
import { getAge } from "../../../services/stringService";
import { pregnancyValidationFn } from "../Components/pregnancyValidationSchema";
import { yupToFormErrors } from "formik";

//TODO: PhoneInputField
const phoneRegExp = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

export const validationSchema = Yup.object().shape({
    serviceStartDate: Yup.date()
        .min(moment("2019-01-01"), `We do not support cases that were created before Jan 1st, 2019.`)
        .required("Required")
        .typeError("Invalid"),
    serviceEndDate: Yup.date()
        .nullable()
        .min(Yup.ref("serviceStartDate"), "Must be greater than service start date."),
    patient: Yup.object().shape({
        firstName: Yup.string()
            .nullable()
            .required("Required"),
        lastName: Yup.string()
            .nullable()
            .required("Required"),
        address: Yup.object().shape({
            line1: Yup.string()
                .nullable()
                .required("Required"),
            city: Yup.string()
                .nullable()
                .required("Required"),
            state: Yup.string()
                .nullable()
                .required("Required"),
            zipCode: Yup.string()
                .nullable()
                .min(5, "Invalid")
                .required("Required"),
        }),
        isMailingAddressDifferent: Yup.bool()
            .nullable()
            .required("Required"),
        isPlanningToMoveToJurisdiction: Yup.bool()
            .nullable()
            .notRequired(),
        primaryPhoneNumber: Yup.object().shape({
            phoneNumber: Yup.string()
                .nullable()
                .required("Required")
                .test("is-valid-phone-number", "Invalid", function (value) {
                    if (value == null || value === "") {
                        return true;
                    }
                    return value?.length === 10;
                }),
            phoneType: Yup.string()
                .when("phoneNumber", {
                    is: val => val?.length === 10,
                    then: Yup.string()
                        .nullable()
                        .required("Required")
                        .test("phoneType", "Required", function (value) {
                            return !value ? false : true;
                        }),
                })
                .nullable(),
        }),

        alternatePhoneNumbers: Yup.array()
            .of(
                Yup.object().shape({
                    phoneNumber: Yup.string()
                        .nullable()
                        .test("is-phone-number-present", "Required", function (value) {
                            return value.length === 10;
                        })
                        .test("is-valid-phone-number", "Invalid", function (value) {
                            return value.length === 10;
                        }),
                    phoneType: Yup.string()
                        .when("phoneNumber", {
                            is: val => val.length === 10,
                            then: Yup.string()
                                .nullable()
                                .required("Required")
                                .test("phoneType", "Required", function (value) {
                                    return !value ? false : true;
                                }),
                        })
                        .nullable(),
                })
            )
            .nullable(),
        mailingAddress: Yup.object()
            .nullable()
            .when("isMailingAddressDifferent", {
                is: true,
                then: Yup.object().shape({
                    line1: Yup.string()
                        .nullable()
                        .required("Required"),
                    city: Yup.string()
                        .nullable()
                        .required("Required"),
                    state: Yup.string()
                        .nullable()
                        .required("Required"),
                    zipCode: Yup.string()
                        .nullable()
                        .min(5, "Invalid")
                        .required("Required"),
                }),
                otherwise: Yup.object()
                    .shape({})
                    .nullable(),
            }),
        email: Yup.string()
            .trim()
            .nullable()
            .optional()
            .email("Invalid"),
        dob: Yup.date()
            .min(
                moment()
                    .subtract(120, "years")
                    .format(),
                `Max 120 years`
            )
            .max(new Date(), `Invalid`)
            .required("Required")
            .typeError("Invalid"),
        isUsCitizen: Yup.bool()
            .nullable()
            .required("Required"),
        immigrationStatus: Yup.string().when("isUsCitizen", {
            is: false,
            then: Yup.string()
                .nullable()
                .required("Required"),
            otherwise: Yup.string().nullable(),
        }),
        usMovingDate: Yup.mixed().when("isUsCitizen", {
            is: false,
            then: Yup.date()
                .min(
                    moment()
                        .subtract(120, "years")
                        .format(),
                    `Max 120 years`
                )
                .nullable()
                .max(new Date(), `Invalid`)
                .typeError("Invalid")
                .required("Invalid"),
            otherwise: Yup.string().nullable(),
        }),

        pregnancy: pregnancyValidationFn("gender", "dob"),
        gender: Yup.string()
            .nullable()
            .required("Required"),
        isMarried: Yup.bool()
            .nullable()
            .required("Required"),
        ssn: Yup.string()
            .nullable()
            .notRequired()
            .test("is-valid-ssn", "Invalid", function (value) {
                // when ssn is entered before landing on page ssn is formatted xxx-xx-xxxx this line removes the -'s
                var val = value?.replace(/[-]+/g, "");
                return val == null || val === "" || val.length === 9;
            }),
    }),
    isResponsiblePartyDifferentFromPatient: Yup.bool()
        .nullable()
        .required("Required"),
    responsibleParty: Yup.object().when("isResponsiblePartyDifferentFromPatient", {
        is: true,
        then: Yup.object().shape({
            firstName: Yup.string()
                .nullable()
                .required("Required"),
            lastName: Yup.string()
                .nullable()
                .required("Required"),
            address: Yup.object().shape({
                line1: Yup.string()
                    .nullable()
                    .required("Required"),
                city: Yup.string()
                    .nullable()
                    .required("Required"),
                state: Yup.string()
                    .nullable()
                    .required("Required"),
                zipCode: Yup.string()
                    .nullable()
                    .min(5, "Invalid")
                    .required("Required"),
            }),
            primaryPhoneNumber: Yup.object().shape({
                phoneNumber: Yup.string()
                    .nullable()
                    .matches(phoneRegExp, "Invalid")
                    .required("Required"),
                phoneType: Yup.string().when("phoneNumber", {
                    is: val => val.length === 10,
                    then: Yup.string()
                        .nullable()
                        .required("Required")
                        .test("phoneType", "Required", function (value) {
                            return !value ? false : true;
                        })
                        .nullable(),
                }),
            }),
            alternatePhoneNumbers: Yup.array()
                .of(
                    Yup.object().shape({
                        phoneNumber: Yup.string()
                            .nullable()
                            .matches(phoneRegExp, "Invalid"),
                        phoneType: Yup.string()
                            .when("phoneNumber", {
                                is: val => val?.length === 10,
                                then: Yup.string()
                                    .nullable()
                                    .required("Required")
                                    .test("phoneType", "Required", function (value) {
                                        return !value ? false : true;
                                    }),
                            })
                            .nullable(),
                    })
                )
                .nullable(),
            isMailingAddressDifferent: Yup.bool()
                .nullable()
                .required("Required"),
            mailingAddress: Yup.object()
                .nullable()
                .when("isMailingAddressDifferent", {
                    is: true,
                    then: Yup.object().shape({
                        line1: Yup.string()
                            .nullable()
                            .required("Required"),
                        city: Yup.string()
                            .nullable()
                            .required("Required"),
                        state: Yup.string()
                            .nullable()
                            .required("Required"),
                        zipCode: Yup.string()
                            .nullable()
                            .min(5, "Invalid")
                            .required("Required"),
                    }),
                    otherwise: Yup.object()
                        .nullable()
                        .shape({}),
                }),
            email: Yup.string()
                .nullable()
                .notRequired()
                .email("Invalid"),
            dob: Yup.date()
                .nullable()
                .min(
                    moment()
                        .subtract(120, "years")
                        .format(),
                    `Max 120 years`
                )
                .max(new Date(), `Invalid`)
                .required("Required")
                .typeError("Invalid"),
            isUsCitizen: Yup.bool()
                .nullable()
                .required("Required"),
            immigrationStatus: Yup.string()
                .nullable()
                .when("isUsCitizen", {
                    is: false,
                    then: Yup.string()
                        .nullable()
                        .required("Required"),
                    otherwise: Yup.string().nullable(),
                }),
            usMovingDate: Yup.mixed().when("isUsCitizen", {
                is: false,
                then: Yup.date()
                    .min(
                        moment()
                            .subtract(120, "years")
                            .format(),
                        `Max 120 years`
                    )
                    .max(new Date(), `Invalid`)
                    .nullable()
                    .typeError("Invalid")
                    .required("Invalid"),
                otherwise: Yup.string().nullable(),
            }),

            gender: Yup.string()
                .nullable()
                .required("Required"),
            pregnancy: pregnancyValidationFn("gender", "dob"),
            isMarried: Yup.bool()
                .nullable()
                .required("Required"),
            doesLiveWithPatientForMoreThan6Months: Yup.bool()
                .nullable()
                .required("Required"),
            relationshipToPatient: Yup.string()
                .nullable()
                .required("Required"),
        }),
        otherwise: Yup.object()
            .shape({})
            .nullable(),
    }),
});
